import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GALLERY, PHOTOS, CONTACT } from "../../lib/site";
import { Reveal } from "../../lib/animations";

export const Entertainment = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);

  return (
    <section
      id="entertainment"
      data-testid="entertainment-section"
      ref={ref}
      className="grain relative overflow-hidden bg-espresso px-6 py-28 md:px-12 md:py-40"
    >
      {/* spotlight */}
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/2 top-1/3 h-[80%] w-[70%] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(228,120,98,0.22),transparent_65%)]"
      />

      <div className="relative mx-auto max-w-[1600px]">
        <Reveal className="mb-2 text-center">
          <p className="font-sans text-[0.7rem] uppercase tracking-[0.4em] text-peach/80">
            Entertainment
          </p>
        </Reveal>
        <Reveal delay={0.05} className="text-center">
          <h2 className="font-serif text-5xl font-light leading-[0.95] tracking-tight text-champagne md:text-8xl lg:text-9xl">
            Balkan <span className="italic text-goldlight">Bellydance</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mx-auto mt-6 max-w-xl text-center">
          <p className="font-sans text-sm leading-relaxed text-peach/70">
            A living gallery of colour, crystal and movement — from festival
            stages to intimate galas across Europe.
          </p>
        </Reveal>

        {/* Editorial staggered gallery */}
        <div className="mt-16 grid grid-cols-2 gap-4 md:mt-24 md:grid-cols-4 md:gap-6">
          {GALLERY.map((img, i) => (
            <motion.figure
              key={img.url}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: i * 0.08 }}
              className={`group relative overflow-hidden border border-gold/25 ${
                i % 2 === 0 ? "md:mt-0" : "md:mt-12"
              } ${i === 1 || i === 2 ? "rounded-t-[120px]" : "rounded-b-[120px]"}`}
            >
              <div className="aspect-[3/4] w-full overflow-hidden">
                <img
                  src={img.url}
                  alt={img.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-110"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </motion.figure>
          ))}
        </div>

        {/* Full-width live band feature */}
        <motion.figure
          data-testid="entertainment-band-feature"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
          className="group relative mt-6 overflow-hidden rounded-3xl border border-gold/25 md:mt-6"
        >
          <div className="aspect-[16/9] w-full overflow-hidden md:aspect-[21/9]">
            <img
              src={PHOTOS.band}
              alt="Cibele performing live with the Nadara Gypsy Band at Mezrab, Amsterdam"
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-105"
            />
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/80 via-espresso/10 to-transparent" />
          <figcaption className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10">
            <p className="font-sans text-[0.6rem] uppercase tracking-[0.35em] text-peach/80">
              Live on stage
            </p>
            <p className="mt-2 font-serif text-2xl italic text-champagne md:text-4xl">
              With the Nadara Gypsy Band — Mezrab, Amsterdam
            </p>
          </figcaption>
        </motion.figure>

        {/* Booking info bar */}
        <Reveal className="relative z-10 mt-20 flex flex-col items-center justify-between gap-8 border-t border-champagne/15 pt-10 md:flex-row">
          <div className="max-w-md text-center md:text-left">
            <p className="font-serif text-2xl italic text-champagne md:text-3xl">
              Unique · energetic · emotional
            </p>
            <p className="mt-3 font-sans text-sm leading-relaxed text-peach/70">
              An unforgettable Balkan-oriental fusion for stages, festivals and
              private events.
            </p>
          </div>
          <div className="text-center md:text-right">
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-peach/70">
              Bookings
            </p>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="entertainment-email"
              className="mt-2 block font-serif text-2xl text-champagne transition-colors duration-300 hover:text-goldlight"
            >
              {CONTACT.email}
            </a>
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="entertainment-whatsapp"
              className="font-sans text-sm tracking-wide text-peach/80 transition-colors duration-300 hover:text-goldlight"
            >
              WhatsApp {CONTACT.whatsapp}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
